import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { getPlayerByUUID, softDeletePlayer, isPlayerDeleted, findDeletedPlayerByEmail, restorePlayerOnSignup, findGuestByEmail, convertGuestToPlayer } from '../services/players';
import { Player } from '../types';
import { initializeLanguage } from '../utils/i18n';
import { isGlobalAdmin as checkIsGlobalAdmin } from '../services/licenses';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  player: Player | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  needsProfileSetup: boolean;
  isProfileComplete: boolean;
  isGlobalAdmin: boolean;
  loadPlayer: (userId: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
  authError: string | null;
  clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [player, setPlayer] = useState<Player | null>(null);
  const [loading, setLoading] = useState(true);
  const [playerLoading, setPlayerLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [authChangeTimeout, setAuthChangeTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isGlobalAdminState, setIsGlobalAdminState] = useState(false);

  useEffect(() => {
    // Fallback timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      console.warn('Auth loading timeout - forcing loading to false');
      setLoading(false);
      setPlayerLoading(false);
    }, 10000); // 10 second timeout

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
        if (session?.user) {
          // Use the same handler for consistency
          handleSignedInUser(session.user.id);
        } else {
          setLoading(false);
        }
    });

    // Listen for auth changes - minimal and non-blocking with debouncing
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state change:', event, session?.user?.email);
        
        // Clear any existing timeout
        if (authChangeTimeout) {
          clearTimeout(authChangeTimeout);
        }
        
        // Prevent recursive calls when we're signing out
        if (isSigningOut && event === 'SIGNED_OUT') {
          setIsSigningOut(false);
          return;
        }
        
        // Debounce auth state changes to prevent rapid-fire events
        const timeout = setTimeout(() => {
          // Only handle significant auth state changes
          if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
            setSession(session);
            setUser(session?.user ?? null);
            
            if (session?.user) {
              // For SIGNED_IN events, check deletion status and load player
              if (event === 'SIGNED_IN') {
                handleSignedInUser(session.user.id);
              } else if (event === 'TOKEN_REFRESHED') {
                // For token refresh, just ensure player is loaded if not already
                if (!player) {
                  handleSignedInUser(session.user.id);
                }
              }
            } else {
              // User signed out
              setPlayer(null);
              setLoading(false);
            }
          }
        }, 25); // 25ms debounce
        
        setAuthChangeTimeout(timeout);
      }
    );

    return () => {
      clearTimeout(loadingTimeout);
      if (authChangeTimeout) {
        clearTimeout(authChangeTimeout);
      }
      subscription.unsubscribe();
    };
  }, []);

  const handleSignedInUser = async (userId: string) => {
    try {
      // Check if the user account is deleted before loading player
      const isDeleted = await isPlayerDeleted(userId);
      if (isDeleted) {
        console.log('User account is deleted, signing out');
        setAuthError('This account has been deleted. Please contact support if you believe this is an error, or sign up again to restore your account.');
        setIsSigningOut(true);
        await supabase.auth.signOut();
        setPlayer(null);
        setLoading(false);
        return;
      }
      
      // Load player data
      await loadPlayer(userId);
    } catch (error) {
      console.error('Error handling signed in user:', error);
      // If there's an error checking deletion status, continue with normal flow
      await loadPlayer(userId);
    }
  };

  const loadPlayer = async (userId: string) => {
    if (playerLoading) {
      return;
    }
    
    setPlayerLoading(true);
    try {
      const playerData = await getPlayerByUUID(userId);
      
      if (playerData) {
        setPlayer(playerData);
        
        // Initialize language from player preference
        if (playerData.language) {
          initializeLanguage(playerData.language);
        }
        
        // Load global admin status
        const adminStatus = await checkIsGlobalAdmin();
        setIsGlobalAdminState(adminStatus);
      } else {
        // No player profile exists yet - create a minimal one
        console.log('No player profile found, creating minimal profile...');
        const { createPlayer } = await import('../services/players');
        
        const minimalPlayer = await createPlayer({
          playerUUID: userId,
          name: '', // Empty - user will see the profile completion hint
          preName: '', // Empty - user will see the profile completion hint
          pin: '', // Empty - user will see the profile completion hint
          email: user?.email || '',
        });
        
        setPlayer(minimalPlayer);
      }
      
      // Use requestAnimationFrame to ensure state update is processed
      requestAnimationFrame(() => {
        setLoading(false);
        setPlayerLoading(false);
      });
    } catch (error) {
      console.error('Error loading player:', error);
      setPlayer(null);
      setLoading(false);
      setPlayerLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    
    // After successful authentication, check if the user's account is deleted
    // This check will happen in the auth state change handler
  };

  const signUp = async (email: string, password: string) => {
    // 1. Check if there's a soft-deleted player with this email
    const deletedPlayer = await findDeletedPlayerByEmail(email);
    
    if (deletedPlayer) {
      // Try to restore the deleted account
      console.log('Found deleted player, attempting restoration:', deletedPlayer.email);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
      
      if (error) throw error;
      
      if (data.user) {
        // Restore the old player profile and link it to the new auth user
        const restored = await restorePlayerOnSignup(deletedPlayer.playerUUID, data.user.id);
        
        if (restored) {
          console.log('Successfully restored player account');
          // The user will be automatically signed in and the restored profile will be loaded
        } else {
          console.error('Failed to restore player account');
        }
      }
      return;
    }

    // 2. Check if there's a guest player with this email
    const guestPlayer = await findGuestByEmail(email);
    
    if (guestPlayer) {
      // Link the guest account
      console.log('Found guest player, attempting to link:', guestPlayer.email);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
      
      if (error) throw error;
      
      if (data.user) {
        // Update guest player to become regular player
        try {
          const linkedPlayer = await convertGuestToPlayer(guestPlayer.id, data.user.id, email, '');
          console.log('✅ Successfully linked guest player to new account:', linkedPlayer);
          // The user will be automatically signed in and the profile will be loaded
        } catch (linkError) {
          console.error('❌ Failed to link guest player:', linkError);
          // User is still created, but linking failed - they'll have a new profile
        }
      }
      return;
    }

    // 3. Normal sign-up flow (no existing player)
    const { error } = await supabase.auth.signUp({
      email,
      password,
    });
    
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const deleteAccount = async () => {
    if (!user) {
      throw new Error('No user logged in');
    }
    
    try {
      // Soft delete the player account
      const success = await softDeletePlayer(user.id);
      
      if (!success) {
        throw new Error('Failed to delete account');
      }
      
      // Note: We cannot directly update auth.users metadata from the client
      // The loadPlayer function will handle checking for deleted accounts
      // and automatically sign out users with deleted profiles
      
      // Sign out the user after successful deletion
      await signOut();
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  };

  const needsProfileSetup = false; // Always allow users to proceed, they'll see the profile completion hint
  // Guest players never have a complete profile (they don't log in)
  const isProfileComplete = Boolean(
    player && 
    !player.is_guest && 
    player.name?.trim() && 
    player.preName?.trim() && 
    player.pin?.trim()
  );

  const clearAuthError = () => {
    setAuthError(null);
  };

  const value: AuthContextType = {
    user,
    session,
    player,
    loading,
    signIn,
    signUp,
    signOut,
    needsProfileSetup,
    isProfileComplete,
    isGlobalAdmin: isGlobalAdminState,
    loadPlayer,
    deleteAccount,
    authError,
    clearAuthError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

